import {
  Form,
  Field,
  FormSizes,
  TextInput,
  Button,
  ButtonVariants,
  ButtonSizes,
  Alert,
  AlertVariants,
  AlertAppearances,
  AlertOrientations,
  Typography,
  TypographyVariants,
  TypographyElements,
  FormValidateOn,
} from '@carboledger/ui';
import { forgotPasswordValidation } from '../authValidation';
import styles from './ForgotPasswordForm.module.scss';
import { useTranslations } from '@/hooks';

/**
 * Props:
 *   onSubmit        ({ email }) => void
 *   onBackToLogin   () => void
 *   isLoading       true while the forgotPassword call is in flight
 *   error           RTK Query error from that call, or undefined
 *   successMessage  the backend's own generic "check your email" message,
 *                    shown once the call succeeds (kept visible so the user
 *                    can still retry with a different address)
 */
export const ForgotPasswordForm = ({
  onSubmit,
  onBackToLogin,
  isLoading,
  error,
  successMessage,
}) => {
  const t = useTranslations();
  return (
    <Form
      validateOn={FormValidateOn.change}
      initialValues={{ email: '' }}
      onSubmit={onSubmit}
      size={FormSizes.small}
      className={styles.root}
    >
      {() => (
        <>
          <Typography
            variant={TypographyVariants.h6}
            as={TypographyElements.h1}
            className={styles.heading}
          >
            {t('AUTH_FORGOT_TITLE')}
          </Typography>

          {error && (
            <Alert
              variant={AlertVariants.danger}
              appearance={AlertAppearances.accent}
              orientation={AlertOrientations.horizontal}
              className={styles.alert}
            >
              {error.data?.message ?? t('ERROR_GENERIC')}
            </Alert>
          )}

          {successMessage && (
            <Alert
              variant={AlertVariants.success}
              appearance={AlertAppearances.accent}
              orientation={AlertOrientations.horizontal}
              className={styles.alert}
            >
              {successMessage}
            </Alert>
          )}

          <div className={styles.fields}>
            <Field name="email" required rules={forgotPasswordValidation.email}>
              {({ id, name, value, onChange, onBlur, status, message, required }) => (
                <TextInput
                  id={id}
                  name={name}
                  label={t('AUTH_FORGOT_EMAIL_LABEL')}
                  placeholder={t('AUTH_FORGOT_EMAIL_PLACEHOLDER')}
                  type="email"
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  status={status}
                  message={message}
                  required={required}
                />
              )}
            </Field>
          </div>

          <div className={styles.actions}>
            <Button type="submit" fullWidth size={ButtonSizes.sm} loading={isLoading}>
              {t('ACTION_CONTINUE')}
            </Button>
            <Button
              type="button"
              variant={ButtonVariants.ghost}
              fullWidth
              size={ButtonSizes.sm}
              onClick={onBackToLogin}
            >
              {t('AUTH_BACK_TO_LOGIN')}
            </Button>
          </div>
        </>
      )}
    </Form>
  );
};
